import { createGlobalStyle } from 'styled-components';

/**
 * Buttons — unified across pages and modals.
 * Yellow (--color-accent) only for primary CTA. Success/danger use green/red tokens.
 */
export const GlobalButtonsStyled = createGlobalStyle`
    .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-2);
        box-sizing: border-box;
        height: var(--control-height);
        min-height: var(--control-height);
        font-family: inherit;
        font-weight: 600;
        font-size: var(--font-size-md);
        line-height: 1.2;
        padding: 0 16px;
        border-radius: var(--radius-md);
        border: 1px solid transparent;
        box-shadow: none;
        transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
    }

    .btn:focus,
    .btn:focus-visible {
        box-shadow: 0 0 0 0.2rem rgba(255, 221, 60, 0.25);
    }

    .btn-sm,
    .btn-group-sm > .btn {
        height: var(--control-height-sm);
        min-height: var(--control-height-sm);
        font-size: var(--font-size-sm);
        line-height: 1.2;
        padding: 0 12px;
        border-radius: var(--radius-md);
    }

    .btn-lg,
    .btn-group-lg > .btn {
        height: var(--control-height-lg);
        min-height: var(--control-height-lg);
        font-size: var(--font-size-base);
        line-height: 1.2;
        padding: 0 24px;
        border-radius: var(--radius-md);
    }

    .btn-group {
        gap: var(--space-2);
    }

    .btn-group > .btn {
        border-radius: var(--radius-md) !important;
    }

    /* Primary CTA — yellow only here (and links) */
    .btn-primary {
        color: var(--text-color-dark);
        background: var(--color-accent);
        border-color: var(--color-accent);

        &:hover,
        &:focus {
            color: var(--text-color-dark);
            background: var(--color-yellow-light);
            border-color: var(--color-yellow-light);
        }

        &:active,
        &.active {
            color: var(--text-color-dark);
            background: var(--color-accent);
            border-color: var(--color-accent);
        }

        &:disabled,
        &.disabled {
            color: var(--text-color-dark);
            background: var(--color-yellow-dark);
            border-color: var(--color-yellow-dark);
            opacity: 1;
        }
    }

    .btn-secondary {
        color: var(--color-white-95);
        background: var(--color-surface-highlight);
        border-color: var(--color-main);

        &:hover,
        &:focus {
            color: var(--color-white);
            background: var(--color-main);
            border-color: var(--color-main);
        }

        &:active,
        &.active {
            color: var(--color-white);
            background: var(--color-main);
            border-color: var(--color-main);
        }

        &:disabled,
        &.disabled {
            color: var(--text-color-secondary);
            background: var(--color-surface-secondary);
            border-color: var(--color-main);
            opacity: 1;
        }
    }

    .btn-success {
        color: var(--color-white);
        background: var(--color-green);
        border-color: var(--color-green);

        &:hover,
        &:focus {
            color: var(--color-white);
            background: var(--color-green-secondary);
            border-color: var(--color-green-secondary);
        }

        &:active,
        &.active {
            color: var(--color-white);
            background: var(--color-green-secondary);
            border-color: var(--color-green-secondary);
        }

        &:disabled,
        &.disabled {
            opacity: 0.55;
        }
    }

    .btn-danger {
        color: var(--color-white);
        background: var(--color-red-secondary);
        border-color: var(--color-red-secondary);

        &:hover,
        &:focus {
            color: var(--color-white);
            background: var(--color-red);
            border-color: var(--color-red);
        }

        &:active,
        &.active {
            color: var(--color-white);
            background: var(--color-red);
            border-color: var(--color-red);
        }

        &:disabled,
        &.disabled {
            opacity: 0.55;
        }
    }

    .btn-warning {
        color: var(--text-color-dark);
        background: var(--color-yellow-light);
        border-color: var(--color-yellow-light);

        &:hover,
        &:focus {
            color: var(--text-color-dark);
            background: var(--color-accent);
            border-color: var(--color-accent);
        }

        &:disabled,
        &.disabled {
            opacity: 0.55;
        }
    }

    .btn-info {
        color: var(--color-white);
        background: #2F3350;
        border-color: #2F3350;

        &:hover,
        &:focus {
            color: var(--color-white);
            background: var(--color-surface-highlight);
            border-color: var(--color-surface-highlight);
        }

        &:disabled,
        &.disabled {
            color: var(--text-color-secondary);
            opacity: 1;
        }
    }

    .btn-outline-primary {
        color: var(--color-accent);
        background: transparent;
        border: 2px solid var(--color-accent);

        &:hover,
        &:focus {
            color: var(--color-yellow-light);
            background: transparent;
            border-color: var(--color-yellow-light);
        }
    }

    .btn-outline,
    .btn-outline-secondary {
        color: var(--color-white-60);
        background: transparent;
        border-color: transparent;

        &:hover,
        &:focus {
            color: var(--color-white-95);
            background: transparent;
        }
    }

    .btn-link {
        color: var(--color-accent);
        background: transparent;
        border-color: transparent;
        font-weight: 600;
        text-decoration: none;
        height: auto;
        min-height: 0;
        padding-left: 0;
        padding-right: 0;

        &:hover,
        &:focus {
            color: var(--color-yellow-light);
            text-decoration: underline;
            background: transparent;
            border-color: transparent;
            box-shadow: none;
        }
    }

    /* Partner-cabinet extras */
    .btn.color-white {
        color: var(--color-white);
    }

    .btn-md,
    .btn-group-md > .btn {
        height: var(--control-height);
        min-height: var(--control-height);
        padding: 0 24px;
        border-radius: var(--radius-lg);
    }

    .btn-outline-danger {
        color: var(--color-negative);
        background: transparent;
        border-color: transparent;

        &:hover,
        &:focus,
        &:active,
        &.active {
            color: var(--color-negative-lite);
            background: transparent;
            border-color: transparent;
        }
    }

    .btn-outline-info {
        color: var(--color-white);
        background: transparent;

        &:hover {
            color: var(--color-white);
            background: var(--color-purple);
            border-color: var(--color-purple);
        }

        &:active,
        &.active {
            color: #64A7FF;
            background: var(--color-purple);
            border-color: var(--color-purple);
        }

        &:disabled {
            color: var(--text-color-secondary);
        }

        &.negative {
            color: var(--color-negative);
        }
    }

    .btn-outline-warning {
        color: var(--color-accent);

        svg {
            transition: fill 0.15s ease-in-out;
            fill: var(--color-accent);
        }

        &:hover {
            background-color: var(--color-accent);
            border-color: var(--color-accent);

            svg {
                fill: var(--color-black);
            }
        }

        &:active svg {
            fill: var(--color-black);
        }

        &:focus {
            box-shadow: 0 0 0 0.25rem rgba(255, 221, 60, 0.5);
        }
    }
`;
